// Japan.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaInstagram, FaEnvelope, FaUser, FaPaperPlane } from 'react-icons/fa';
import { FaTiktok } from 'react-icons/fa6';
import contactUsBg from '../assets/Sonic New/34.jpeg';
import personImage from '../assets/Sonic New/att.XLunJMj7sPQJgNc4efT9_ZXLER_W4UhhoDTruiRIPnA.JPG';

// Motion variants
const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Japan = () => {
    const { t } = useTranslation('japan');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactNumber: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        const recipientEmail = 'mtakahashi@sonictourssl.com';
        const subject = encodeURIComponent('Sonic Tours Japan Inquiry');
        const body = encodeURIComponent(`Name: ${formData.name}
Email: ${formData.email}
Contact Number: ${formData.contactNumber}

Message:
${formData.message}`);
        const mailtoUrl = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;

        try {
            window.location.href = mailtoUrl;
            setTimeout(() => {
                setIsSubmitting(false);
                setSuccessMessage(t('successMsg'));
                setFormData({ name: '', email: '', contactNumber: '', message: '' });
            }, 1000);
        } catch (error) {
            setIsSubmitting(false);
            setErrorMessage(t('errorMsg'));
        }
    };

    return (
        <div className="bg-gradient-to-br from-[#001F3F] via-[#003366] to-[#001F3F] min-h-screen">
            {/* HERO SECTION */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[70vh] w-full items-center justify-center font-lato md:h-[60vh] lg:h-[85vh] overflow-hidden"
            >
                <img
                    src={contactUsBg}
                    alt="Contact Sonic Tours Japan Office"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-[#001F3F]/70 to-black/85" />

                <div className="container relative z-10 flex flex-col items-center justify-center pt-[80px] px-4">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        className="mb-6"
                    >
                        <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-[#0074D9] to-[#7FDBFF] rounded-full p-1 shadow-2xl">
                            <div className="w-full h-full bg-white rounded-full flex items-center justify-center">
                                <span className="text-4xl md:text-5xl">🇯🇵</span>
                            </div>
                        </div>
                    </motion.div>

                    <h1 className="text-center text-[45px] font-bold leading-[60px] text-[#7FDBFF] font-konkhmer md:text-[65px] lg:text-[85px] md:leading-[80px] lg:leading-[95px]">
                        {t('heroTitle')}
                    </h1>
                    <p className="mt-6 max-w-[90%] text-center font-inknut text-lg font-medium text-white/90 sm:mt-10 sm:max-w-3xl sm:text-2xl">
                        {t('heroSubtitle')}
                    </p>

                    {/* Decorative Elements */}
                    <div className="absolute left-6 top-12 md:left-10 md:top-16 opacity-60">
                        <svg width="280" height="240" viewBox="0 0 280 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M280 1H1V239.5" stroke="url(#gradient1)" strokeWidth="2" />
                            <defs>
                                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#0074D9" />
                                    <stop offset="100%" stopColor="#7FDBFF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <div className="absolute -bottom-12 right-6 md:-bottom-16 md:right-10 opacity-60">
                        <svg width="280" height="240" viewBox="0 0 280 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 239.5H279V0" stroke="url(#gradient2)" strokeWidth="2" />
                            <defs>
                                <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#7FDBFF" />
                                    <stop offset="100%" stopColor="#0074D9" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>
            </motion.div>

            {/* CONTACT PERSON SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-16 mt-6 sm:mt-6 md:mt-10 lg:mt-10 relative z-10"
            >
                <div className="container mx-auto px-4">
                    <motion.div
                        variants={fadeInUp}
                        className="max-w-5xl mx-auto bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-white/20 mt-10"
                    >
                        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                            {/* Person Image */}
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                whileInView={{ scale: 1, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="flex-shrink-0"
                            >
                                <div className="relative">
                                    <div className="absolute -inset-4 bg-gradient-to-r from-[#0074D9] to-[#7FDBFF] rounded-full blur-2xl opacity-50 animate-pulse"></div>
                                    <img
                                        src={personImage}
                                        alt="Tomoya Izawa - Japan Representative"
                                        className="relative w-48 h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 rounded-full object-cover object-[center_20%] border-4 border-white/30 shadow-2xl"
                                    />
                                    <div className="absolute bottom-2 right-2 bg-gradient-to-r from-[#0074D9] to-[#7FDBFF] rounded-full p-3 shadow-lg">
                                        <FaUser className="w-6 h-6 text-white" />
                                    </div>
                                </div>
                            </motion.div>

                            {/* Person Details */}
                            <div className="flex-grow text-center lg:text-left">
                                <motion.h2
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.3 }}
                                    className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-konkhmer mb-3"
                                >
                                    {t('contactPerson')}
                                </motion.h2>
                                <motion.p
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.4 }}
                                    className="text-xl md:text-2xl text-[#7FDBFF] font-inknut mb-6"
                                >
                                    {t('position')}
                                </motion.p>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.5 }}
                                    className="hidden"
                                />
                            </div>
                        </div>

                        {/* Social Links */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.6 }}
                            className="mt-10 pt-8 border-t border-white/20"
                        >
                            <p className="text-center text-white/80 text-lg font-inknut mb-5">
                                {t('socialMedia')}
                            </p>
                            <div className="flex justify-center gap-6">
                                <a href="https://www.instagram.com/sonic_tours.srilanka?igsh=c213Mjdob2t6aWQ%3D" target="_blank" rel="noopener noreferrer"
                                    className="group bg-white/10 hover:bg-gradient-to-r hover:from-[#0074D9] hover:to-[#7FDBFF] p-4 rounded-xl transition-all duration-300 transform hover:scale-110">
                                    <FaInstagram className="w-7 h-7 text-white" />
                                </a>
                                <a href="mailto:mtakahashi@sonictourssl.com"
                                    className="group bg-white/10 hover:bg-gradient-to-r hover:from-[#0074D9] hover:to-[#7FDBFF] p-4 rounded-xl transition-all duration-300 transform hover:scale-110">
                                    <FaEnvelope className="w-7 h-7 text-white" />
                                </a>
                                <a href="https://www.tiktok.com/@sonic_tours?lang=en-GB" target="_blank" rel="noopener noreferrer"
                                    className="group bg-white/10 hover:bg-gradient-to-r hover:from-[#0074D9] hover:to-[#7FDBFF] p-4 rounded-xl transition-all duration-300 transform hover:scale-110">
                                    <FaTiktok className="w-7 h-7 text-white" />
                                </a>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* CONTACT FORM SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-16 lg:py-24"
            >
                <div className="container mx-auto px-4">
                    <motion.div
                        variants={fadeInUp}
                        className="max-w-3xl mx-auto"
                    >
                        <div className="text-center mb-12">
                            <h2 className="text-[#0074D9] text-2xl md:text-3xl font-medium tracking-wider font-inknut mb-4">
                                {t('formTitle')}
                            </h2>
                            <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-bold leading-tight font-konkhmer">
                                {t('formHeading')}
                            </h1>
                            <p className="text-white/70 text-lg md:text-xl leading-relaxed mt-6 font-inknut max-w-2xl mx-auto">
                                {t('formDescription')}
                            </p>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-white/20"
                        >
                            <form className="space-y-7" onSubmit={handleSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-white text-base md:text-lg font-semibold tracking-wide mb-3 font-inknut">
                                            {t('nameLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full bg-white/10 border-2 border-white/30 rounded-xl py-4 px-5 text-white text-base outline-none focus:border-[#7FDBFF] focus:bg-white/20 transition-all duration-300 placeholder-white/50"
                                            placeholder={t('namePlaceholder')}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-white text-base md:text-lg font-semibold tracking-wide mb-3 font-inknut">
                                            {t('emailLabel')}
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full bg-white/10 border-2 border-white/30 rounded-xl py-4 px-5 text-white text-base outline-none focus:border-[#7FDBFF] focus:bg-white/20 transition-all duration-300 placeholder-white/50"
                                            placeholder={t('emailPlaceholder')}
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-white text-base md:text-lg font-semibold tracking-wide mb-3 font-inknut">
                                        {t('phoneLabel')}
                                    </label>
                                    <input
                                        type="tel"
                                        name="contactNumber"
                                        value={formData.contactNumber}
                                        onChange={handleChange}
                                        className="w-full bg-white/10 border-2 border-white/30 rounded-xl py-4 px-5 text-white text-base outline-none focus:border-[#7FDBFF] focus:bg-white/20 transition-all duration-300 placeholder-white/50"
                                        placeholder={t('phonePlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-white text-base md:text-lg font-semibold tracking-wide mb-3 font-inknut">
                                        {t('messageLabel')}
                                    </label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        className="w-full bg-white/10 border-2 border-white/30 rounded-xl py-4 px-5 text-white text-base outline-none focus:border-[#7FDBFF] focus:bg-white/20 transition-all duration-300 resize-none placeholder-white/50"
                                        rows={6}
                                        placeholder={t('messagePlaceholder')}
                                        required
                                    />
                                </div>

                                {successMessage && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-[#7FDBFF] text-base md:text-lg font-medium bg-[#7FDBFF]/20 p-4 rounded-xl border border-[#7FDBFF]/50"
                                    >
                                        ✓ {successMessage}
                                    </motion.p>
                                )}
                                {errorMessage && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-red-400 text-base md:text-lg font-medium bg-red-400/20 p-4 rounded-xl border border-red-400/50"
                                    >
                                        ✗ {errorMessage}
                                    </motion.p>
                                )}

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`mt-6 flex h-16 w-full items-center justify-center gap-3 rounded-2xl px-10 font-bold text-white text-lg md:text-xl transition-all duration-300 shadow-2xl font-konkhmer ${isSubmitting
                                        ? 'opacity-70 cursor-not-allowed scale-95'
                                        : 'hover:scale-105 hover:shadow-[0_0_30px_rgba(127,219,255,0.5)]'
                                        }`}
                                    style={{
                                        background: isSubmitting
                                            ? 'linear-gradient(135deg, #001F3F, #003366)'
                                            : 'linear-gradient(135deg, #0074D9, #7FDBFF, #0074D9)',
                                        backgroundSize: '200% 200%',
                                        animation: isSubmitting ? 'none' : 'gradient 3s ease infinite',
                                    }}
                                >
                                    <FaPaperPlane className="w-6 h-6" />
                                    <span>{isSubmitting ? t('sendingButton') : t('sendButton')}</span>
                                    {!isSubmitting && (
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-6 w-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" />
                                        </svg>
                                    )}
                                </button>
                            </form>

                            <style jsx>{`
                                @keyframes gradient {
                                    0% { background-position: 0% 50%; }
                                    50% { background-position: 100% 50%; }
                                    100% { background-position: 0% 50%; }
                                }
                            `}</style>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* QUICK INFO SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-16 lg:py-20 bg-gradient-to-r from-[#0074D9]/20 to-[#7FDBFF]/20 backdrop-blur-sm"
            >
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-16 text-white">
                            {t('whyChooseTitle')}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105"
                            >
                                <div className="text-5xl mb-5">🌏</div>
                                <h3 className="text-2xl font-bold text-white mb-4 font-konkhmer">{t('expertiseTitle')}</h3>
                                <p className="text-white/80 text-base leading-relaxed font-inknut">
                                    {t('expertiseDesc')}
                                </p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2 }}
                                className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105"
                            >
                                <div className="text-5xl mb-5">💬</div>
                                <h3 className="text-2xl font-bold text-white mb-4 font-konkhmer">{t('supportTitle')}</h3>
                                <p className="text-white/80 text-base leading-relaxed font-inknut">
                                    {t('supportDesc')}
                                </p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 }}
                                className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105"
                            >
                                <div className="text-5xl mb-5">⭐</div>
                                <h3 className="text-2xl font-bold text-white mb-4 font-konkhmer">{t('personalizedTitle')}</h3>
                                <p className="text-white/80 text-base leading-relaxed font-inknut">
                                    {t('personalizedDesc')}
                                </p>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* MAP SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-16 lg:py-20"
            >
                <div className="container mx-auto px-4">
                    <h2 className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-12 text-white">
                        {t('mapTitle')}
                    </h2>
                    <div className="flex justify-center">
                        <div className="w-full max-w-6xl">
                            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.5407035388994!2d79.97928619999999!3d6.4529558!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae22f0076863e37%3A0x3cfc9d6916d65722!2sK%20D%20S%20Stores!5e0!3m2!1sen!2slk!4v1771206342326!5m2!1sen!2slk"
                                    width="100%"
                                    height="500"
                                    className="border-0"
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Sonic Tours Sri Lanka Location"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default Japan;
